import React, { useEffect, useState } from 'react'
import { useTranslation } from '../hooks/use-i18n'

interface Formula1ExistedPositionModalProps {
  isOpen: boolean
  position: number
  driverName: string
  onKeepClick: () => void
  onChangeClick: () => void
  /** When false, only "Keep" is shown (e.g. race already completed). */
  canChange?: boolean
}

const positionSuffix = (position: number) => {
  if (position === 1) return 'st'
  if (position === 2) return 'nd'
  if (position === 3) return 'rd'
  return 'th'
}

const Formula1ExistedPositionModal: React.FC<Formula1ExistedPositionModalProps> = ({
  isOpen,
  position,
  driverName,
  onKeepClick,
  onChangeClick,
  canChange = true,
}) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShow(true), 200)
      return () => clearTimeout(timer)
    }
    setShow(false)
  }, [isOpen])

  if (!show || !isOpen) return null

  const positionLabel = `${position}${positionSuffix(position)}`
  const title = t('formula1.youPredictedPosition', {
    position: positionLabel,
    driver: driverName,
  })

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="blue-sky-card rounded-t-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 w-full sm:max-w-md sm:mx-4 shadow-2xl animate-scale-in max-h-[85vh] sm:max-h-[80vh] overflow-y-auto sm:mt-16 lg:mt-20">
        <div className="sm:hidden w-12 h-1 bg-white/30 rounded-full mx-auto mb-4" />

        <div className="text-center mb-4 sm:mb-5">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
            {t('formula1.playingRoomPredictions')}
          </h2>
          <p className="text-white/80 font-semibold text-sm sm:text-base mt-2">{title}</p>
          <p className="text-white/60 text-xs sm:text-sm mt-1">
            {t('predictions.alreadyPlayed')}
          </p>
        </div>

        <div className="bg-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-5">
          <p className="text-white font-medium text-center">
            {positionLabel} place: <span className="font-semibold">{driverName}</span>
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => {
              onKeepClick()
              setShow(false)
            }}
            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary hover:opacity-90 active:scale-95 text-primary-foreground rounded-xl transition-all font-semibold text-sm sm:text-base"
          >
            {t('predictions.keep')}
          </button>
          {canChange && (
            <button
              onClick={() => {
                onChangeClick()
                setShow(false)
              }}
              className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary hover:opacity-90 active:scale-95 text-primary-foreground rounded-xl transition-all font-semibold text-sm sm:text-base"
            >
              {t('predictions.change')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Formula1ExistedPositionModal
